import { motion } from "framer-motion";
import { ExternalLink, Ticket, CalendarDays } from "lucide-react";
import { EVENT_POSTERS, CONTACT, WEEKLY_EVENTS } from "@/data/site";
import useApiCollection from "@/lib/useApiCollection";
import { imageUrl } from "@/lib/api";

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  show: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.55, delay: i * 0.07, ease: "easeOut" },
  }),
};

export default function Events() {
  // Pull events from the admin panel. If empty, fall back to the static posters
  // so the live site is never blank during migration.
  const { data: adminEvents } = useApiCollection("/api/events");
  const posters =
    adminEvents && adminEvents.length > 0
      ? adminEvents.map((e) => ({
          img: imageUrl(e.image_url),
          date: e.date,
          title: e.title,
          description: e.description,
        }))
      : EVENT_POSTERS;

  return (
    <div data-testid="events-page" className="bg-tnt-bg">
      {/* Header */}
      <section className="relative py-20 sm:py-28 px-4 sm:px-6 lg:px-8 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-tnt-surface via-tnt-bg to-tnt-bg" />
        <div className="absolute -top-40 -left-32 w-[500px] h-[500px] bg-tnt-gold/8 rounded-full blur-3xl" />
        <div className="absolute -bottom-40 -right-32 w-[500px] h-[500px] bg-tnt-gold/10 rounded-full blur-3xl" />

        <div className="relative mx-auto max-w-4xl">
          <div className="inline-flex items-center gap-2 text-tnt-gold font-display tracking-[0.3em] uppercase text-xs mb-4">
            <CalendarDays className="w-4 h-4" />
            <span>live at taps &amp; tunes</span>
          </div>
          <h1 className="font-display text-5xl sm:text-7xl lg:text-8xl text-tnt-ink tracking-tight leading-[0.95]">
            UPCOMING
            <br />
            <span className="gold-text">EVENTS</span>
          </h1>

          <p className="mt-8 text-tnt-ink/85 text-lg leading-relaxed max-w-2xl">
            Looking for upcoming shows, comedy nights, live music, and special
            events at Taps &amp; Tunes?
          </p>
          <p className="mt-4 text-tnt-muted max-w-2xl">
            Check back regularly for the latest events, ticket releases, and
            special announcements.
          </p>

          <div className="mt-8 flex flex-col sm:flex-row gap-3">
            <a
              href={CONTACT.linktree}
              target="_blank"
              rel="noreferrer"
              data-testid="events-tickets-cta"
              className="btn-gold group"
            >
              <Ticket className="w-4 h-4 mr-2" />
              Get Your Tickets
              <ExternalLink className="w-4 h-4 ml-2 opacity-70 group-hover:translate-x-0.5 transition-transform" />
            </a>
            <a
              href={CONTACT.linktree}
              target="_blank"
              rel="noreferrer"
              data-testid="events-linktree-link"
              className="inline-flex items-center text-tnt-ink/70 hover:text-tnt-gold transition-colors font-sans text-sm self-center"
            >
              linktr.ee/tapsandtunesbarandgrill
            </a>
          </div>
        </div>
      </section>

      {/* Weekly recap strip */}
      <section
        data-testid="weekly-strip"
        className="px-4 sm:px-6 lg:px-8 py-12 border-y border-tnt-border bg-tnt-surface"
      >
        <div className="mx-auto max-w-7xl">
          <p className="font-display text-tnt-gold tracking-[0.3em] uppercase text-xs">
            Every Week at Taps &amp; Tunes
          </p>
          <div className="mt-4 flex flex-wrap gap-x-8 gap-y-3">
            {WEEKLY_EVENTS.map((e) => (
              <div
                key={e.day}
                data-testid={`events-week-${e.day.toLowerCase()}`}
                className="flex items-baseline gap-3"
              >
                <span className="font-display text-xl text-tnt-gold tracking-widest uppercase">
                  {e.day.slice(0, 3)}
                </span>
                <span className="text-tnt-ink/80 font-sans text-sm">
                  {e.title}
                  {e.time && (
                    <span className="text-tnt-ink/40"> • {e.time}</span>
                  )}
                </span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Poster Gallery */}
      <section
        data-testid="poster-gallery-section"
        className="wood-bg px-4 sm:px-6 lg:px-8 py-20 sm:py-28 bg-tnt-bg"
      >
        <div className="mx-auto max-w-7xl">
          <div className="flex items-end justify-between flex-wrap gap-4 mb-12">
            <div>
              <p className="font-display text-tnt-gold tracking-[0.3em] uppercase text-xs">
                On the Wall
              </p>
              <h2 className="font-display text-4xl sm:text-5xl text-tnt-ink tracking-tight mt-2">
                EVENT <span className="gold-text">POSTERS</span>
              </h2>
            </div>
            <p className="text-tnt-muted max-w-md">
              A glimpse at what&apos;s coming up. New posters added regularly —
              keep an eye out.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 sm:gap-6">
            {posters.map((p, i) => (
              <motion.figure
                key={(p.title || "") + i}
                custom={i}
                variants={fadeUp}
                initial="hidden"
                whileInView="show"
                viewport={{ once: true, amount: 0.2 }}
                data-testid={`event-poster-${i}`}
                className="group relative overflow-hidden rounded-2xl border border-tnt-border bg-black"
              >
                {p.img ? (
                  <img
                    src={p.img}
                    alt={p.title}
                    loading="lazy"
                    decoding="async"
                    className="block w-full h-auto object-contain transition-transform duration-700 group-hover:scale-[1.01]"
                  />
                ) : (
                  <div className="aspect-[4/5] w-full bg-tnt-surface2" />
                )}
                <figcaption className="p-4 sm:p-5 border-t border-white/10">
                  <p className="font-display text-tnt-gold tracking-widest uppercase text-xs">
                    {p.date}
                  </p>
                  <h3 className="font-display text-lg sm:text-xl text-white tracking-wide leading-tight mt-1">
                    {p.title}
                  </h3>
                  {p.description && (
                    <p className="text-white/65 text-sm mt-1.5 line-clamp-3">
                      {p.description}
                    </p>
                  )}
                </figcaption>
              </motion.figure>
            ))}
          </div>

          <p className="mt-12 text-center text-sm text-tnt-ink/50 italic">
            Tickets, announcements &amp; more on our{" "}
            <a
              href={CONTACT.linktree}
              target="_blank"
              rel="noreferrer"
              data-testid="events-bottom-linktree"
              className="text-tnt-gold hover:underline"
            >
              Linktree
            </a>
            .
          </p>
        </div>
      </section>
    </div>
  );
}
